import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Observable, switchMap } from 'rxjs';

import { Page, NavService } from '@intranet/core/services/nav.service';
import { LoginService } from '@intranet/modules/login';
import { TimerAppDownloadService } from '@intranet/core/services/timer-app-download.service';

@Component({
    selector: 'nav-menu',
    templateUrl: './navmenu.component.html',
    styleUrls: ['./navmenu.component.scss'],
})
export class NavMenuComponent implements OnInit {
    sidenav$: Observable<Page[]>;
    expanded: boolean = false;
    /** True when there's an unseen newer Timer-app version. Drives the red
     *  dot badge on the Settings nav icon — mirrors the same indicator on
     *  the timer-tracker page's download button and the settings page's
     *  Download button. */
    timerAppHasUpdate$!: Observable<boolean>;

    /** id of the parent nav item whose submenu is currently opened via
     *  tap/click — the desktop/tablet hover reveal doesn't exist on touch
     *  screens, so this is the touch fallback for showing children. */
    openSubmenuId: string | null = null;

    /** Matches $tablet in _breakpoints.scss — below this width `.Nav`/`.Nav-list`
     *  clip overflow to make the icon strip horizontally scrollable, which also
     *  clips the inline submenu dropdown. Below that width we render the open
     *  submenu through a CDK overlay (see submenuOverlayPositions) instead, so
     *  it escapes that clipping ancestor. */
    private static readonly MOBILE_NAV_MAX_WIDTH = 660;

    isMobileNav: boolean = typeof window !== 'undefined'
        && window.innerWidth <= NavMenuComponent.MOBILE_NAV_MAX_WIDTH;

    readonly submenuOverlayPanelClass = 'nav-submenu-overlay-pane';
    readonly submenuOverlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    ];

    @HostListener('window:resize')
    onWindowResize(): void {
        this.isMobileNav = window.innerWidth <= NavMenuComponent.MOBILE_NAV_MAX_WIDTH;
    }

    constructor(
        public loginService: LoginService,
        private service: NavService,
        public router: Router,
        public nav: ActivatedRoute,
        private timerAppDownload: TimerAppDownloadService,
    ) {}

    ngOnInit(): void {
        this.sidenav$ = this.nav.paramMap.pipe(
            switchMap((params: ParamMap) => {
                return this.service.getPages();
            })
        );

        // Reactive — re-emits when the user clicks Download anywhere (the
        // service pushes the new seen-version into its internal subject),
        // so the badge clears immediately without needing a page refresh.
        this.timerAppHasUpdate$ = this.timerAppDownload.newVersionAvailable$();
    }

    getNavItemClasses(page: Page, isActive: boolean = false): { [key: string]: boolean } {
      const classes: { [key: string]: boolean } = {
        'has-submenu': !!page.children
      };

      if (page.children) {
        const id = page.id || page.name.toLowerCase().replace(/\s+/g, '-');
        classes[`has-submenu--${id}`] = true;
      }

      if (isActive) {
        classes['Nav-link--active'] = true;
      }

      if (page.children && this.openSubmenuId === page.id) {
        classes['is-open'] = true;
      }

      return classes;
    }

    toggleSubmenu(pageId: string, event: Event): void {
      event.stopPropagation();
      this.openSubmenuId = this.openSubmenuId === pageId ? null : pageId;
    }

    @HostListener('document:click')
    closeSubmenu(): void {
      this.openSubmenuId = null;
    }

    isExactChildActive(parentId: string, childId: string): boolean {
      const currentUrl = this.router.url;
      return currentUrl === `/${parentId}/${childId}`;
    }

    isChildActive(page: Page): boolean {
      if (!page.children || !this.router.url) return false;

      return page.children.some(child => {
        const fullPath = `/${page.id}/${child.id}`;
        return this.router.url.startsWith(fullPath);
      });
    }

    expandStatus(status) {
        this.expanded = status;
    }

    logout() {
        this.loginService.logoutAzure();
    }
}
